import { useQuery } from "@tanstack/react-query";
import { invoices } from "../services/api";
import { InvoiceStatus, InvoiceType } from "../types";
import type { Invoice, HaulierLineItem, WarehouseLineItem } from "../types";

interface InvoiceDetailData {
  invoice: Invoice;
  haulier_lines: HaulierLineItem[];
  warehouse_lines: WarehouseLineItem[];
}

/**
 * TanStack Query hook for fetching a single invoice with its line items.
 *
 * When the invoice status is PROCESSING, it automatically polls every 10 seconds
 * so the UI updates in near-real-time as backend processing progresses.
 * Polling stops once the status leaves PROCESSING.
 */
export function useInvoice(id: string | undefined) {
  return useQuery<InvoiceDetailData>({
    queryKey: ["invoice", id],
    queryFn: async () => {
      if (!id) throw new Error("Invoice ID is required");
      const raw = await invoices.get(id);
      const lineItems = raw.line_items ?? [];

      // Split the flat line_items array into typed arrays
      const haulier_lines =
        raw.invoice.invoice_type === InvoiceType.HAULIER
          ? (lineItems as HaulierLineItem[])
          : [];
      const warehouse_lines =
        raw.invoice.invoice_type === InvoiceType.WAREHOUSE
          ? (lineItems as WarehouseLineItem[])
          : [];

      return { invoice: raw.invoice, haulier_lines, warehouse_lines };
    },
    enabled: !!id,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.invoice.status === InvoiceStatus.PROCESSING) {
        return 10_000;
      }
      return false;
    },
  });
}
